import { defineStore } from 'pinia'

export type Nullable<T> = T | null;


export interface Message {
  content: string,
  status: string
}

export interface ResponseList<T> {
  count: number,
  results: T[]
}

export interface SailingClub {
  id: number,
  name: string,
  country: Nullable<number>,
  country_details: Nullable<Country>
}

export interface User {
  id?: number
  name: string
  slug: string
  claimable?: boolean
  sailing_club?: Nullable<number>
  sailing_club_details: Nullable<SailingClub>
  groups?: { id: number; name: string }[]
  preferred_speed_unit?: 'kn' | 'ms'
  avatar_url?: Nullable<string>
  stats?: Nullable<UserStats>
  is_coach?: boolean
}

export interface CoachingUser {
  id: number
  name: string
  initials: string
  avatar_url: Nullable<string>
}

export interface CoachingLink {
  id: number
  coach: CoachingUser
  student: CoachingUser
  created: string
  student_last_upload: Nullable<string>
}

export interface Stats {
  [key: string]: number
}

export interface UserStats {
  distance_nm: number
  hours: number
  top_speed_kn: number
  boats: Boat[]
  countries: Country[]
}

export interface Country {
  id?: number
  name?: string
  iso3166: string
}

export interface Post {
  id: number,
  title: string,
  publish_date: string,
  preview_image_url: string,
  subtitle: string,
  meta_description: string,
  slug: string,
  teaser: string,
  content: string,
  author: { user?: { name: string } },
  featured: boolean
}

export interface GeoJsonPoint {
  type: string,
  coordinates: Array<number>
}

export interface GeoJsonLineString {
  type: string,
  coordinates: Array<Array<number>>
}

export interface GeoJsonPolygon {
  type: string,
  coordinates: Array<Array<Array<number>>>
}

export interface GeoJSONFeature<T, U> {
  id?: number,
  geometry: U,
  properties: T,
  type: string
}

export interface Leg {
  id: number,
  start_coordinate: Array<number>,
  end_coordinate: Array<number>,
  geojson: GeoJSONFeature<{}, GeoJsonLineString>
}

export interface WindAnalysis {
  duration_s: number,
  vmg_ms: number,
  legs: Array<Leg>
}

export interface Boat {
  id: number,
  name: string,
  slug: string,
  child_details: Array<Object>,
  category?: string,
  get_category_display?: string,
}

export interface Venue {
  id: number,
  name: string,
  slug: string,
  countries: Array<Country>,
  description: string,
  hero_image_url: Nullable<string>,
}

export interface VenueComment {
  id: number,
  user_details: Nullable<User>,
  text: string,
  created: string,
}

export interface CourseMark {
  id?: number,
  order?: number,
  type: string,
  longitude1: number,
  latitude1: number,
  longitude2: number,
  latitude2: number,
  marker_class?: string,
}

export interface CourseLeg {
  leg_index: number,
  wind_direction_deg: Nullable<number>,
  wind_speed: Nullable<number>,
  tide_direction_deg: Nullable<number>,
  tide_speed: Nullable<number>,
}

export interface Course {
  id?: number,
  user: Nullable<number>,
  competition?: Nullable<number>,
  marks: CourseMark[],
  legs: CourseLeg[],
  course_code?: Nullable<string>,
  course_distance_m?: Nullable<number>,
  // Order the marks are sailed, as CourseMark ids (empty = stored order) -- see courseLegMarks.
  route?: number[],
}

export interface Maneuver {
  id: number
  maneuver_type: string
  maneuver_type_display: string
  timestamp: string
  latitude: number
  longitude: number
  source: string
  is_confirmed: boolean
  is_deleted: boolean
  tack_after: string | null
  entry_heading: number | null
  exit_heading: number | null
  duration_s: number | null
  speed_before_ms: number | null
  speed_after_ms: number | null
  min_speed_ms: number | null
  note: string
  analysis: Nullable<{
    entry_speed_ms: number | null
    exit_speed_ms: number | null
    min_speed_ms: number | null
    time_axis: number[]
    sog_series: Array<number | null>
    tack?: {
      height_change_m: number | null
      tack_angle: number | null
      vmg_series: Array<number | null>
      sog_series: Array<number | null>
      heel_series: Array<number | null> | null
      direction: string | null
      body_movement_speed?: number | null
      clip_url?: string | null
      shoulder_timeseries?: Array<{ t: number, x: number }>
    }
  }>
}

export interface Activity {
  id: number,
  name: string,
  user: number,
  upload: Nullable<{
    id: number,
    segments: Array<{ id: number, name: string, start_time: string, end_time: string, order: number, activity_id: number | null }>,
    handicapping_list: Nullable<number>,
    handicapping_list_details: Nullable<HandicappingList & { id: number }>,
    // The specific boat sailed, with its sail number (bulk uploads only); null
    // when the upload only picked a boat class.
    user_boat_details: Nullable<{ id: number, sail_number: string, boat_details: Nullable<Boat> }>,
  }>,
  user_details: User,
  boat_details: Nullable<Boat>,
  geojson: GeoJSONFeature<Object, GeoJsonLineString>,
  simplified_geojson: GeoJSONFeature<Object, GeoJsonLineString>,
  start_time: string,
  end_time: string,
  duration_s: number,
  video: Nullable<string>,
  time: Array<number>,
  heading_deg: Array<number>,
  speed_ms: Array<number>,
  wind_speed_knots: Nullable<number>,
  user_wind_direction: Nullable<number>,
  wind_speed: Nullable<number>,
  wind_speed_unit: Nullable<number>,
  comment: string,
  upwind_analysis: WindAnalysis,
  reach_analysis: WindAnalysis,
  reach_speed_ms?: Nullable<number>,
  downwind_analysis: WindAnalysis,
  race: Nullable<number>,
  race_start?: Nullable<string>,
  // performance.Race (RaceSerializer), not a competition race.
  race_details: Nullable<{ id: number, start: Nullable<string>, course_details: Nullable<Course> }>,
  distance_m: number,
  max_speed_ms: Nullable<number>,
  scoring_wind_direction: Nullable<number>,
  scoring_speed_upwind_starboard_ms:  Nullable<number>,
  course_details: Nullable<Course>,
  video_poster_url: Nullable<string>,
  video_poster_blurred_url: Nullable<string>,
  sensor_time: Nullable<Array<number>>,
  heel_rad: Nullable<Array<number | null>>,
  pitch_rad: Nullable<Array<number | null>>,
  compass_heading_deg: Nullable<Array<number | null>>,
  sensor_hz: Nullable<number>,
  ble_wind_direction: Nullable<Array<number | null>>,
  ble_wind_speed: Nullable<Array<number | null>>,
  ext_timeseries: Nullable<Array<{ key: string, unit: string, time: Array<number>, values: Array<number> }>>,
  coach_opener: Nullable<string>,
  analysis: Nullable<object>,
  analysis_updating: boolean,
  league_race_rank: Nullable<{ rank: number, total: number }>,
  // False when the activity is private (free-user upload, or privacy toggled off).
  // Only the owner, a linked coach, or staff receive rows where this is false.
  visible?: boolean,
  // Whether the requesting user may edit this activity (owner or staff).
  // Only present on the activity-detail endpoint.
  can_edit?: boolean,
}

// Multi-track course analysis (SavedAnalysisSerializer).
export interface SavedAnalysis {
  uuid: string,
  owner: number,
  owner_details: User,
  title: string,
  analysis_activities: Array<{ activity: number, order: number, time_offset_ms: number }>,
  course_features: Array<any>,
  // Order the course features are sailed, as their properties.mark_id values (empty = in order).
  course_route?: number[],
  race_start: Nullable<string>,
  handicapping_list: Nullable<number>,
  handicapping_list_details: Nullable<HandicappingList & { id: number }>,
  comments: Array<{ id: number, user_details: User, text: string, created: string }>,
  created: string,
}

export enum HandicappingSystem {
  OD = 'One-Design',
  DF = 'Division Factor',
  YS = 'Yardstick',
  PY = 'Portsmouth Yardstick',
  PF = 'PHRF',
}

export interface HandicappingList {
  id?: number,
  name: string,
  handicapping_system: string,
  get_handicapping_system_display?: string,
  // Time-on-Time coefficients, only used when handicapping_system === 'PF':
  // TCF = tcf_a / (tcf_b + PHRF rating), corrected_time = elapsed_time * TCF.
  tcf_a?: Nullable<number>,
  tcf_b?: Nullable<number>,
}

export interface ScoredLeagueRace {
  id: number
  scoring_activity: number
  scoring_activity_details: Activity
  user_details: User
  boat_details: Boat
  calibration_wind_direction: number
  start_finish_longitude1: number
  start_finish_latitude1: number
  start_finish_longitude2: number
  start_finish_latitude2: number
  upwind_mark_longitude: number
  upwind_mark_latitude: number
  scoring_laps: number
  scoring_start: string
  upwind_analysis: WindAnalysis
  downwind_analysis: WindAnalysis
  scoring_speed_ms: Nullable<number>
  scoring_flag: Nullable<String>
}

export interface LeagueRaceResult {
  id: number,
  user_details: User
  race: number
  race_details: ScoredLeagueRace
  scoring_activity: number
  scoring_activity_details: Activity
}

export interface SkipperMonthlyRanking {
  points: number,
}


export const useMainStore = defineStore('main', {
  state: () => ({
    // Fixed categorical order (blue, orange, aqua, yellow, magenta, green,
    // violet, red) — CVD-safe for adjacent pairs; do not reorder or cycle.
    colors: ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'],
    speedUnits: [
      { name: 'knots', value: 'kn' },
      { name: 'km/h', value: 'kmh' },
      { name: 'm/s', value: 'ms' }
    ] as Array<Object>,
    message: null as Nullable<Message>,
  }),
  actions: {
    setMessage(m: Message){
      this.message = m
      return m
    }
  },
})
